import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { calculateFontSize } from '../../constants/renderConfig';

/**
 * ASCIIPreview Component
 *
 * Displays the ASCII art with hover-to-freeze functionality and fullscreen modal.
 */
const ASCIIPreview = ({
  asciiText,
  columns,
  onFreeze,
  onUnfreeze,
  isFrozen,
  backgroundColor,
  foregroundColor,
  isFullscreen = false,
  onFullscreenClose
}) => {
  const { colors } = useTheme();

  useEffect(() => {
    if (!isFullscreen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onFullscreenClose?.();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, onFullscreenClose]);

  return (
    <>
    {/* Fullscreen Modal */}
    {isFullscreen && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: colors.background,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        animation: 'fadeIn 0.2s ease-out'
      }}>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>

        {/* Header with Close Button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: spacing.lg,
          borderBottom: `1px solid ${colors.border}`,
          background: colors.backgroundPanel,
          flexShrink: 0
        }}>
          <span style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: colors.foreground
          }}>
            ASCII Preview (Full Size)
          </span>
          <button
            onClick={onFullscreenClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: borderRadius.sm,
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.foreground,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.backgroundOffset;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            aria-label="Close fullscreen preview"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Preview Area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xxl
        }}>
          <pre style={{
            fontFamily: 'monospace',
            fontSize: calculateFontSize(columns) + 'px',
            lineHeight: '1',
            letterSpacing: '0.05em',
            color: foregroundColor || colors.foreground,
            userSelect: 'text',
            margin: 0,
            padding: 0,
            whiteSpace: 'pre'
          }}>
            {asciiText}
          </pre>
        </div>

        {/* Hint Text */}
        <div style={{
          padding: spacing.md,
          textAlign: 'center',
          fontSize: '0.8125rem',
          color: colors.foregroundSecondary,
          background: colors.backgroundPanel,
          borderTop: `1px solid ${colors.border}`
        }}>
          Scroll to view full preview • Press ESC to close
        </div>
      </div>
    )}

    <div style={{
      position: 'relative',
      display: 'inline-block',
      background: backgroundColor || 'transparent',
      padding: backgroundColor ? spacing.md : 0,
      borderRadius: backgroundColor ? '8px' : 0,
      transition: 'background 0.2s'
    }}>
      <pre
        onMouseEnter={onFreeze}
        onMouseLeave={onUnfreeze}
        style={{
          fontFamily: 'monospace',
          fontSize: calculateFontSize(columns) + 'px',
          lineHeight: '1',
          letterSpacing: '0.05em',
          color: foregroundColor || colors.foreground,
          userSelect: 'none',
          margin: 0,
          padding: 0,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'opacity 0.2s, color 0.2s',
          opacity: isFrozen ? 0.95 : 1
        }}
      >
        {asciiText}
      </pre>
    </div>
    </>
  );
};

export default ASCIIPreview;
